import { useState, type CSSProperties, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { useClientConfig } from '../lib/useClientConfig';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { SUPPORT_EMAIL, SUPPORT_LOCATION, SUPPORT_RESPONSE_TIME } from '../lib/support';

// The contact form posts to POST /v1/contact, which emails support (TOM-98,
// ADR 0280). The answer says what became of the message: `accepted` when the
// email went, `suppressed` off production, where nothing is emailed.
type Status = 'idle' | 'sending' | 'sent' | 'suppressed' | 'error';

interface ContactResponse {
  ok: true;
  delivery: 'accepted' | 'suppressed';
}

const linkButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'inherit',
  font: 'inherit',
  textDecoration: 'underline',
  cursor: 'pointer',
};

export function Contact() {
  useDocumentTitle('Contact us - Neomaris');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  // Turnstile (threat model E6): the same runtime site-key plumbing as signup
  // and password reset; null in local dev and tests means no widget.
  const {
    config: clientConfig,
    loaded: configLoaded,
    error: configError,
    reload: reloadConfig,
  } = useClientConfig();
  const turnstileSiteKey = clientConfig?.turnstileSiteKey ?? null;
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileNonce, setTurnstileNonce] = useState(0);
  const [turnstileWidgetKey, setTurnstileWidgetKey] = useState(0);
  const [turnstileFailed, setTurnstileFailed] = useState(false);

  const canSubmit =
    status !== 'sending' &&
    configLoaded &&
    !configError &&
    (turnstileSiteKey === null || turnstileToken !== null);

  function retryTurnstile() {
    setTurnstileFailed(false);
    setTurnstileWidgetKey((k) => k + 1);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('sending');
    setErrorMessage(null);
    try {
      const body: Record<string, string> = { name, email, subject, message };
      if (turnstileToken !== null) body['cf-turnstile-response'] = turnstileToken;
      const res = await api<ContactResponse>('/v1/contact', { method: 'POST', body });
      setStatus(res.delivery === 'accepted' ? 'sent' : 'suppressed');
    } catch (err) {
      const e = err as { status?: number; body?: { message?: string }; message?: string };
      // The token was spent on this attempt; fetch a fresh one for the retry.
      if (turnstileSiteKey !== null) {
        setTurnstileToken(null);
        setTurnstileNonce((n) => n + 1);
      }
      setStatus('error');
      setErrorMessage(
        e.status === 429
          ? `You've sent several messages in the last hour. Please wait, or email ${SUPPORT_EMAIL}.`
          : (e.body?.message ?? `Something went wrong. Please email ${SUPPORT_EMAIL}.`)
      );
    }
  }

  return (
    <>
      <section className="page-hero">
        <h1 className="reveal">Get in touch</h1>
        <p className="reveal reveal-delay-1">
          Have a question about Neomaris? We&apos;d love to hear from you.
        </p>
      </section>

      <section className="section section--tight-top">
        <div className="section-inner">
          <div className="contact-grid reveal">
            <div className="contact-form">
              {status === 'sent' ? (
                <div className="form-success" role="status" data-testid="contact-sent">
                  <h3>Thanks, we&apos;ll be in touch.</h3>
                  <p>
                    Your message is on its way to {SUPPORT_EMAIL}. {SUPPORT_RESPONSE_TIME}
                  </p>
                </div>
              ) : status === 'suppressed' ? (
                <div className="form-success" role="status" data-testid="contact-suppressed">
                  <h3>Message received, not emailed.</h3>
                  <p>
                    This is a test copy of Neomaris, which doesn&apos;t send email. To reach us,
                    write to {SUPPORT_EMAIL}.
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-form">
                  {status === 'error' && errorMessage !== null ? (
                    <ul className="form-errors" role="alert" data-testid="contact-error">
                      <li>{errorMessage}</li>
                    </ul>
                  ) : null}
                  {configError ? (
                    <ul className="form-errors" role="alert">
                      <li>
                        We couldn&apos;t load verification.{' '}
                        <button type="button" style={linkButtonStyle} onClick={reloadConfig}>
                          Retry
                        </button>
                        , or email {SUPPORT_EMAIL}.
                      </li>
                    </ul>
                  ) : null}
                  <div className="form-group">
                    <label htmlFor="id_name">Name</label>
                    <input
                      type="text"
                      name="name"
                      id="id_name"
                      className="form-input"
                      placeholder="Your name"
                      autoComplete="name"
                      maxLength={200}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="id_email">Email</label>
                    <input
                      type="email"
                      name="email"
                      id="id_email"
                      className="form-input"
                      placeholder="you@example.com"
                      autoComplete="email"
                      maxLength={254}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="id_subject">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      id="id_subject"
                      className="form-input"
                      placeholder="What's this about?"
                      maxLength={200}
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="id_message">Message</label>
                    <textarea
                      name="message"
                      id="id_message"
                      className="form-textarea"
                      placeholder="Tell us what's on your mind..."
                      maxLength={5000}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                    />
                  </div>
                  {turnstileSiteKey !== null && !turnstileFailed ? (
                    <div className="form-group">
                      <TurnstileWidget
                        key={turnstileWidgetKey}
                        siteKey={turnstileSiteKey}
                        onToken={setTurnstileToken}
                        onExpire={() => setTurnstileToken(null)}
                        onError={() => setTurnstileFailed(true)}
                        resetSignal={turnstileNonce}
                      />
                    </div>
                  ) : null}
                  {turnstileFailed ? (
                    <ul className="form-errors" role="alert">
                      <li>
                        We couldn&apos;t finish a quick security check, usually a weak connection.{' '}
                        <button type="button" style={linkButtonStyle} onClick={retryTurnstile}>
                          Retry
                        </button>
                        , or email {SUPPORT_EMAIL}.
                      </li>
                    </ul>
                  ) : null}
                  <button
                    type="submit"
                    className="btn btn-primary auth-submit"
                    disabled={!canSubmit}
                  >
                    {status === 'sending' ? 'Sending…' : 'Send message'}
                  </button>
                </form>
              )}
            </div>

            <div className="contact-info">
              <h3>Other ways to reach us</h3>

              <div className="contact-detail">
                <div className="contact-detail-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div className="contact-detail-text">
                  <h4>Email</h4>
                  <p>
                    <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                  </p>
                </div>
              </div>

              <div className="contact-detail">
                <div className="contact-detail-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div className="contact-detail-text">
                  <h4>Based in</h4>
                  <p>{SUPPORT_LOCATION}</p>
                </div>
              </div>

              <div className="contact-detail">
                <div className="contact-detail-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="contact-detail-text">
                  <h4>Response time</h4>
                  <p>{SUPPORT_RESPONSE_TIME}</p>
                </div>
              </div>

              <div className="contact-detail">
                <div className="contact-detail-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <div className="contact-detail-text">
                  <h4>Help with the app</h4>
                  <p>
                    Pairing, gateway setup, alarms and your account:{' '}
                    <Link to="/support">see Support</Link>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
